import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
  Alert,
} from "antd";
import type { TableProps } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import "remixicon/fonts/remixicon.css";
import { useGlobal } from "../contexts/GlobalContext";
import { useCurrentProject } from "../hooks/useCurrentProject";
import request from "../utils/request";
import { getPageContext } from "../utils/pageContext";
import { streamJson } from "../utils/sse";
import type { ApiResponse } from "../types/api";

const { Paragraph, Text, Title } = Typography;
const MESSAGE_WINDOW_SIZE = 120;
const MESSAGE_WINDOW_STEP = 120;

interface AgentRecord {
  id: number;
  project_id?: number;
  name: string;
  description?: string;
  icon?: string;
  agent_type?: string;
  status?: string;
  system_prompt?: string;
  model?: string;
  knowledge_base_ids?: number[];
  cache_config?: Record<string, unknown>;
  is_public?: boolean;
  is_system_default?: boolean;
  system_key?: string;
  runtimeProfile?: string;
  share_enable?: boolean;
  share_token?: string;
  updated_at?: string;
  created_at?: string;
}

interface AgentListResult {
  items: AgentRecord[];
  total: number;
  page: number;
  size: number;
  quota?: ProjectAgentQuota;
}

interface ProjectAgentQuota {
  projectId: number;
  limit: number;
  used: number;
  remaining: number;
  min: number;
  isEnforced: boolean;
}

interface PublicModel {
  id: number;
  modelName: string;
  displayName?: string;
  modelType?: string;
  maxTokens?: number;
  contextWindow?: number;
  isDefault?: boolean;
}

interface ConversationItem {
  id: number;
  agentId: number;
  title: string;
  variables?: Record<string, unknown>;
  messageCount: number;
  tokenUsed: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ConversationListResult {
  items: ConversationItem[];
  total: number;
  page: number;
  size: number;
}

interface AgentMessage {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  tokenUsed: number;
  modelName?: string;
  durationMs?: number;
  createdAt: string;
}

interface AgentRunResult {
  agentId: number;
  conversationId: number;
  messageId?: number;
  status: string;
  output?: Record<string, unknown>;
  answer?: string;
  references?: Array<Record<string, unknown>>;
  modelUsed?: string;
  costCents?: number;
  cached?: boolean;
}

interface AgentFormValues {
  template?: string;
  projectId: number;
  name: string;
  description?: string;
  icon?: string;
  agentType: string;
  model?: string;
  systemPrompt?: string;
  knowledgeBaseIds?: number[];
  cacheEnabled?: boolean;
  cacheTtl?: number;
  isPublic?: boolean;
}

const WORKFLOW_AGENT_TYPE = "workflow";
const DEFAULT_KNOWLEDGE_AGENT_KEY = "default_knowledge_agent";

// 与后端 agent_type 枚举一致：chatbot 对话型 / workflow 工作流型 / completion 补全型
const isDefaultKnowledgeAgent = (agent?: AgentRecord | null) =>
  Boolean(agent?.is_system_default || agent?.system_key === DEFAULT_KNOWLEDGE_AGENT_KEY);

const formatAgentType = (value?: string) => {
  if (!value || value === "chatbot") {
    return "对话";
  }
  if (value === WORKFLOW_AGENT_TYPE) {
    return "工作流";
  }
  if (value === "completion") {
    return "补全";
  }
  return value;
};

const isWorkflowAgent = (agent?: AgentRecord | null) =>
  agent?.agent_type === WORKFLOW_AGENT_TYPE;

const agentTemplates = [
  {
    value: "knowledge",
    label: "知识库问答（工作流）",
    name: "知识库问答助手",
    description: "基于当前项目知识库回答访客问题",
    icon: "ri-book-open-line",
    agentType: WORKFLOW_AGENT_TYPE,
    systemPrompt: "你是知识库问答助手。优先基于提供的知识回答；知识不足时明确说明。",
  },
  {
    value: "chat",
    label: "普通对话",
    name: "对话助手",
    description: "处理自由问答、咨询和轻量任务（系统提示词 + 工具 + 知识库）",
    icon: "ri-robot-2-line",
    agentType: "chatbot",
    systemPrompt: "你是一个可靠、简洁的 AI 助手。",
  },
  {
    value: "workflow",
    label: "可视化工作流",
    name: "工作流助手",
    description: "用节点编排模型、知识库、工具和条件分支",
    icon: "ri-flow-chart",
    agentType: WORKFLOW_AGENT_TYPE,
    systemPrompt: "你是一个可按流程执行任务的 AI 助手。",
  },
  {
    value: "completion",
    label: "纯文本补全",
    name: "文本补全助手",
    description: "适合固定输入输出和可缓存的文本生成",
    icon: "ri-file-text-line",
    agentType: "completion",
    systemPrompt: "你是一个高质量文本生成助手。",
  },
];

const iconOptions = [
  { label: "机器人", value: "ri-robot-2-line" },
  { label: "知识库", value: "ri-book-open-line" },
  { label: "流程", value: "ri-flow-chart" },
  { label: "客服", value: "ri-customer-service-2-line" },
  { label: "文本", value: "ri-file-text-line" },
  { label: "工具", value: "ri-tools-line" },
];

const statusMap: Record<string, { label: string; color: string }> = {
  draft: { label: "草稿", color: "default" },
  active: { label: "已发布", color: "success" },
  archived: { label: "已归档", color: "warning" },
};

const Agents: React.FC = () => {
  const { API_BASE_URL, token, projects } = useGlobal();
  const { project: routeProject } = useCurrentProject();
  const navigate = useNavigate();
  const [form] = Form.useForm<AgentFormValues>();
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentRecord | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<number>();
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [selectedConversationId, setSelectedConversationId] =
    useState<number>();
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [visibleMessageCount, setVisibleMessageCount] = useState(MESSAGE_WINDOW_SIZE);
  const [runQuery, setRunQuery] = useState("");
  const [runLoading, setRunLoading] = useState(false);
  const [runResult, setRunResult] = useState<AgentRunResult | null>(null);
  const [streamingText, setStreamingText] = useState("");
  const [streamingReasoning, setStreamingReasoning] = useState("");
  const runControllerRef = useRef<AbortController | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number>();
  const [models, setModels] = useState<PublicModel[]>([]);
  const [projectQuota, setProjectQuota] = useState<ProjectAgentQuota>();

  useEffect(() => {
    setVisibleMessageCount(MESSAGE_WINDOW_SIZE);
  }, [selectedConversationId, messages.length]);

  const visibleMessages = useMemo(() => {
    if (messages.length <= visibleMessageCount) return messages;
    return messages.slice(messages.length - visibleMessageCount);
  }, [messages, visibleMessageCount]);

  const hiddenMessageCount = Math.max(messages.length - visibleMessages.length, 0);

  const selectedAgent = useMemo(
    () => agents.find((item) => item.id === selectedAgentId) || null,
    [agents, selectedAgentId]
  );

  const postChatProjects = useMemo(
    () => projects.filter((project) => project.is_postchat),
    [projects]
  );

  const knowledgeOptions = useMemo(
    () =>
      postChatProjects.map((project) => ({
        label: project.name,
        value: project.id,
      })),
    [postChatProjects]
  );

  const projectOptions = useMemo(
    () =>
      postChatProjects.map((project) => ({
        label: project.name,
        value: project.id,
      })),
    [postChatProjects]
  );

  const modelOptions = useMemo(
    () =>
      models.map((model) => ({
        label: model.displayName
          ? `${model.displayName} (${model.modelName})`
          : model.modelName,
        value: model.modelName,
      })),
    [models]
  );

  const defaultModelName = useMemo(() => {
    return (
      models.find((model) => model.isDefault)?.modelName ||
      models[0]?.modelName ||
      "gpt-4o-mini"
    );
  }, [models]);

  // 服务端模板库(07§4.2):/api/v3/agents/templates,失败时回落本地内置
  const [serverTemplates, setServerTemplates] = useState<typeof agentTemplates>([]);
  useEffect(() => {
    request
      .get(`${API_BASE_URL}/api/v3/agents/templates`)
      .then((res) => {
        const items = res.data?.data || [];
        if (!Array.isArray(items) || items.length === 0) return;
        setServerTemplates(
          items
            .filter((t: any) => !t.disabled)
            .map((t: any) => ({
              value: t.key,
              label: t.name,
              name: t.name,
              description: t.description || "",
              icon: t.icon || (t.agentType === WORKFLOW_AGENT_TYPE ? "ri-flow-chart" : "ri-robot-2-line"),
              agentType: t.agentType || "chatbot",
              systemPrompt: t.systemPrompt || "",
            }))
        );
      })
      .catch(() => {});
  }, [API_BASE_URL]);
  // One project has one primary Agent. Workflow templates stay out of the
  // console; prompt variants are configured on the primary Agent.
  const effectiveTemplates = (serverTemplates.length > 0 ? serverTemplates : agentTemplates)
    .filter((template) => template.agentType === "chatbot");

  const applyAgentTemplate = useCallback(
    (templateValue: string, projectId?: number) => {
      const template =
        effectiveTemplates.find((item) => item.value === templateValue) ||
        effectiveTemplates[0];
      form.setFieldsValue({
        template: template.value,
        name: template.name,
        description: template.description,
        icon: template.icon,
        agentType: "chatbot",
        model: defaultModelName,
        systemPrompt: template.systemPrompt,
        cacheEnabled: template.value === "completion",
        cacheTtl: 3600,
        knowledgeBaseIds: projectId ? [projectId] : [],
      });
    },
    [defaultModelName, form, effectiveTemplates]
  );

  useEffect(() => {
    setSelectedProjectId((current) => {
      const pid = routeProject?.id;
      if (pid && postChatProjects.some((project) => project.id === pid)) {
        return pid;
      }
      if (current && postChatProjects.some((project) => project.id === current)) {
        return current;
      }
      return postChatProjects[0]?.id;
    });
  }, [routeProject?.id, postChatProjects]);

  useEffect(() => {
    if (!token) {
      return;
    }

    request
      .get<ApiResponse<PublicModel[]>>(`${API_BASE_URL}/api/v3/models`)
      .then((response) => {
        if (response.data.code === 200) {
          setModels(response.data.data || []);
        }
      })
      .catch((error) => {
        console.error("获取模型列表失败:", error);
      });
  }, [API_BASE_URL, token]);

  const fetchAgents = useCallback(async () => {
    if (!token) {
      return;
    }

    if (!selectedProjectId) {
      setAgents([]);
      setProjectQuota(undefined);
      setSelectedAgentId(undefined);
      return;
    }

    setLoading(true);
    try {
      const response = await request.get<ApiResponse<AgentListResult>>(
        `${API_BASE_URL}/api/v3/agents`,
        {
          params: {
            page: 1,
            pageSize: 50,
            project_id: selectedProjectId,
          },
        }
      );

      if (response.data.code === 200) {
        const nextAgents = response.data.data.items || [];
        setAgents(nextAgents);
        setProjectQuota(response.data.data.quota);
        setSelectedAgentId((current) => {
          if (current && nextAgents.some((item) => item.id === current)) {
            return current;
          }
          return nextAgents[0]?.id;
        });
      } else {
        message.error(response.data.msg || "获取智能体列表失败");
      }
    } catch (error) {
      console.error("获取智能体列表失败:", error);
      message.error("获取智能体列表失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, selectedProjectId, token]);

  const fetchConversations = useCallback(
    async (agentId: number) => {
      if (!token) {
        return;
      }

      setConversationsLoading(true);
      try {
        const response = await request.get<ApiResponse<ConversationListResult>>(
          `${API_BASE_URL}/api/v3/agents/${agentId}/conversations`,
          {
            params: {
              page: 1,
              pageSize: 50,
            },
          }
        );

        if (response.data.code === 200) {
          const nextConversations = response.data.data.items || [];
          setConversations(nextConversations);
          setSelectedConversationId((current) => {
            if (
              current &&
              nextConversations.some((item) => item.id === current)
            ) {
              return current;
            }
            return nextConversations[0]?.id;
          });
        } else {
          message.error(response.data.msg || "获取对话列表失败");
        }
      } catch (error) {
        console.error("获取对话列表失败:", error);
        message.error("获取对话列表失败，请稍后重试");
      } finally {
        setConversationsLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  const fetchMessages = useCallback(
    async (conversationId: number) => {
      if (!token) {
        return;
      }

      setMessagesLoading(true);
      try {
        const response = await request.get<ApiResponse<AgentMessage[]>>(
          `${API_BASE_URL}/api/v3/agents/conversations/${conversationId}/messages`
        );

        if (response.data.code === 200) {
          setMessages(response.data.data || []);
        } else {
          message.error(response.data.msg || "获取消息记录失败");
        }
      } catch (error) {
        console.error("获取消息记录失败:", error);
        message.error("获取消息记录失败，请稍后重试");
      } finally {
        setMessagesLoading(false);
      }
    },
    [API_BASE_URL, token]
  );

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  useEffect(() => {
    if (selectedAgentId) {
      fetchConversations(selectedAgentId);
      return;
    }

    setConversations([]);
    setSelectedConversationId(undefined);
    setMessages([]);
    setRunResult(null);
    setStreamingText("");
    setStreamingReasoning("");
  }, [selectedAgentId, fetchConversations]);

  // 卸载时中断流式请求
  useEffect(() => {
    return () => {
      runControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
      return;
    }

    setMessages([]);
  }, [selectedConversationId, fetchMessages]);

  const openCreateModal = () => {
    const defaultProjectId = selectedProjectId || postChatProjects[0]?.id;
    if (!defaultProjectId) {
      message.warning("请先创建或选择 PostChat 项目");
      return;
    }
    setEditingAgent(null);
    form.resetFields();
    form.setFieldsValue({
      template: "chat",
      projectId: defaultProjectId,
      isPublic: false,
      agentType: "chatbot",
    });
    applyAgentTemplate("chat", defaultProjectId);
    setModalVisible(true);
  };

  const openEditModal = (agent: AgentRecord) => {
    const cacheConfig = agent.cache_config || {};
    setEditingAgent(agent);
    form.setFieldsValue({
      template: "custom",
      projectId: agent.project_id || agent.knowledge_base_ids?.[0],
      name: agent.name,
      description: agent.description,
      icon: agent.icon,
      agentType: agent.agent_type || "chatbot",
      model: agent.model,
      systemPrompt: agent.system_prompt,
      knowledgeBaseIds: agent.knowledge_base_ids || [],
      cacheEnabled: !!cacheConfig.enable,
      cacheTtl: Number(cacheConfig.ttl || 3600),
      isPublic: !!agent.is_public,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const targetProject = postChatProjects.find(
        (project) => project.id === values.projectId
      );
      if (!targetProject) {
        message.warning("仅 PostChat 项目支持创建和编辑智能体");
        return;
      }
      setSubmitting(true);

      const payload: Record<string, unknown> = {
        name: values.name,
        projectId: values.projectId,
        description: values.description || "",
        icon: values.icon || "",
        systemPrompt: values.systemPrompt || "",
        model: values.model || defaultModelName,
        cacheConfig: {
          enable: !!values.cacheEnabled,
          ttl: values.cacheTtl || 3600,
          scope: "project",
        },
        knowledgeBaseIds: values.knowledgeBaseIds?.length
          ? values.knowledgeBaseIds
          : [values.projectId],
        isPublic: !!values.isPublic,
      };
      // agentType 仅在创建时生效（后端按类型选择对话/工作流运行时），更新接口不支持改类型
      if (!editingAgent) {
        payload.agentType = values.agentType || "chatbot";
      }

      const response = editingAgent
        ? await request.put(
            `${API_BASE_URL}/api/v3/agents/${editingAgent.id}`,
            payload
          )
        : await request.post(`${API_BASE_URL}/api/v3/agents`, payload);

      if (response.data.code === 200) {
        message.success(editingAgent ? "智能体更新成功" : "智能体创建成功");
        setModalVisible(false);
        await fetchAgents();
      } else {
        message.error(
          response.data.msg || (editingAgent ? "更新失败" : "创建失败")
        );
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error("提交智能体失败:", error);
      message.error(error.response?.data?.msg || "提交智能体失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgent = (agent: AgentRecord) => {
    if (isDefaultKnowledgeAgent(agent)) {
      message.warning("默认知识库 Agent 不能删除，可在配置页一键恢复默认");
      return;
    }
    Modal.confirm({
      title: "确认删除智能体",
      icon: <ExclamationCircleOutlined />,
      content: `删除后将无法恢复：${agent.name}`,
      okText: "确认删除",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: async () => {
        try {
          const response = await request.delete(
            `${API_BASE_URL}/api/v3/agents/${agent.id}`
          );

          if (response.data.code === 200) {
            message.success("智能体已删除");
            if (selectedAgentId === agent.id) {
              setSelectedAgentId(undefined);
            }
            await fetchAgents();
          } else {
            message.error(response.data.msg || "删除失败");
          }
        } catch (error: any) {
          console.error("删除智能体失败:", error);
          message.error(error.response?.data?.msg || "删除失败，请重试");
        }
      },
    });
  };

  const handleToggleStatus = async (agent: AgentRecord) => {
    const targetAction = agent.status === "active" ? "archive" : "publish";

    try {
      const response = await request.post(
        `${API_BASE_URL}/api/v3/agents/${agent.id}/${targetAction}`,
        {}
      );

      if (response.data.code === 200) {
        message.success(
          targetAction === "publish" ? "智能体已发布" : "智能体已归档"
        );
        await fetchAgents();
      } else {
        message.error(response.data.msg || "状态更新失败");
      }
    } catch (error: any) {
      console.error("更新智能体状态失败:", error);
      message.error(error.response?.data?.msg || "状态更新失败，请重试");
    }
  };

  const finishRun = useCallback(
    async (agentId: number, conversationId: number) => {
      if (conversationId) {
        setSelectedConversationId(conversationId);
        await fetchConversations(agentId);
        await fetchMessages(conversationId);
      }
    },
    [fetchConversations, fetchMessages]
  );

  const handleRunAgent = async () => {
    if (!selectedAgent) {
      message.warning("请先选择一个智能体");
      return;
    }

    const query = runQuery.trim();
    if (!query) {
      message.warning("请输入运行问题或指令");
      return;
    }

    // 工作流型走非流式（可拿到完整 output）；对话/补全型走 SSE 流式
    if (isWorkflowAgent(selectedAgent)) {
      setRunLoading(true);
      try {
        const response = await request.post<ApiResponse<AgentRunResult>>(
          `${API_BASE_URL}/api/v3/agents/${selectedAgent.id}/run`,
          {
            query,
            input: { input: query },
            conversationId: selectedConversationId || 0,
            stream: false,
            pageContext: getPageContext(),
          }
        );

        if (response.data.code === 200) {
          const result = response.data.data;
          setRunResult(result);
          setStreamingText("");
          setStreamingReasoning("");
          setRunQuery("");
          await finishRun(selectedAgent.id, result.conversationId);
          message.success("智能体运行完成");
        } else {
          message.error(response.data.msg || "运行失败");
        }
      } catch (error: any) {
        console.error("运行智能体失败:", error);
        message.error(error.response?.data?.msg || "运行失败，请重试");
      } finally {
        setRunLoading(false);
      }
      return;
    }

    // 流式运行
    setRunLoading(true);
    setRunResult(null);
    setStreamingText("");
    setStreamingReasoning("");
    let conversationId = selectedConversationId || 0;
    let accumulated = "";
    let reasoning = "";
    try {
      runControllerRef.current?.abort();
      runControllerRef.current = new AbortController();
      await streamJson<any>(`${API_BASE_URL}/api/v3/agents/${selectedAgent.id}/run`, {
        method: "POST",
        token,
        body: JSON.stringify({
          query,
          input: {},
          conversationId,
          stream: true,
          pageContext: getPageContext(),
        }),
        signal: runControllerRef.current.signal,
        onJson: (data) => {
          if (data.conversationId) {
            conversationId = Number(data.conversationId);
          }
          if (data.reasoningContent) {
            reasoning += data.reasoningContent;
            setStreamingReasoning(reasoning);
          }
          if (data.content) {
            accumulated += data.content;
            setStreamingText(accumulated);
          }
        },
        onerror: (err) => {
          throw err;
        },
      });

      setRunResult({
        agentId: selectedAgent.id,
        conversationId,
        status: "completed",
        answer: accumulated,
      });
      setRunQuery("");
      await finishRun(selectedAgent.id, conversationId);
    } catch (error: any) {
      console.error("流式运行失败:", error);
      message.error(error?.message || "运行失败，请重试");
    } finally {
      setRunLoading(false);
    }
  };

  const handleDeleteConversation = async (conversationId: number) => {
    try {
      const response = await request.delete(
        `${API_BASE_URL}/api/v3/agents/conversations/${conversationId}`
      );

      if (response.data.code === 200) {
        message.success("对话已删除");
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(undefined);
        }
        if (selectedAgentId) {
          await fetchConversations(selectedAgentId);
        }
      } else {
        message.error(response.data.msg || "删除对话失败");
      }
    } catch (error: any) {
      console.error("删除对话失败:", error);
      message.error(error.response?.data?.msg || "删除对话失败，请重试");
    }
  };

  const columns: TableProps<AgentRecord>["columns"] = [
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      render: (_value, record) => (
        <Space size={10}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: record.id === selectedAgentId ? "#e6f4ff" : "#f5f5f5",
              color: record.id === selectedAgentId ? "#1677ff" : "#666",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
            }}
          >
            <i className={record.icon || "ri-robot-2-line"} />
          </div>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Space size={4} wrap>
              {isDefaultKnowledgeAgent(record) && <Tag color="gold">默认知识库</Tag>}
              {record.runtimeProfile && <Tag color="blue">{record.runtimeProfile}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.model || "未配置模型"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "类型",
      dataIndex: "agent_type",
      key: "agent_type",
      render: (value: string) => formatAgentType(value),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      render: (value: string) => {
        const status = statusMap[value] || { label: value || "未知", color: "default" };
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: "公开",
      dataIndex: "is_public",
      key: "is_public",
      render: (value: boolean) => (
        <Tag color={value ? "blue" : "default"}>{value ? "公开" : "私有"}</Tag>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: string) =>
        value ? new Date(value).toLocaleString("zh-CN") : "-",
    },
    {
      title: "操作",
      key: "actions",
      render: (_value, record) => (
        <Space>
          <Button size="small" onClick={() => openEditModal(record)}>
            编辑
          </Button>
          {true && (
            <Button
              size="small"
              type="link"
              onClick={() => navigate(`/agents/${record.id}/config`)}
            >
              <i className="ri-settings-3-line" style={{ marginRight: 4 }} />
              配置
            </Button>
          )}
          <Button size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === "active" ? "归档" : "发布"}
          </Button>
          <Button
            size="small"
            danger
            disabled={isDefaultKnowledgeAgent(record)}
            onClick={() => handleDeleteAgent(record)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        background: "#f0f2f5",
        minHeight: "100%",
        borderRadius: 8,
        padding: "16px",
      }}
    >
      <Card
        title={
          <Space>
            <i className="ri-robot-2-line" style={{ color: "#1677ff", fontSize: 20 }} />
            <span>智能体</span>
          </Space>
        }
        extra={
          <Space>
            <Button onClick={fetchAgents}>刷新</Button>
            <Tag color="blue" style={{ height: 32, lineHeight: "30px", padding: "0 12px" }}>
              {routeProject?.name || "—"}
            </Tag>
            <Button
              type="primary"
              onClick={openCreateModal}
              disabled={
                !selectedProjectId || agents.length > 0 || (!!projectQuota && projectQuota.remaining <= 0)
              }
            >
              新建项目主 Agent
            </Button>
          </Space>
        }
        variant="outlined"
      >
        {postChatProjects.length === 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="当前没有可创建智能体的 PostChat 项目"
            description="仅 PostChat 项目支持智能体；Summary 项目可在首页「转置」为 PostChat 项目。"
          />
        )}
        {selectedProjectId && agents.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="每个项目仅保留一个主 Agent"
            description="Copilot、知识库、工具和系统提示词都在主 Agent 上配置；不同角色请在配置页维护提示词变体，不再创建子 Agent 或工作流。"
          />
        )}
        {projectQuota && (
          <Alert
            type={projectQuota.remaining > 0 ? "info" : "warning"}
            showIcon
            style={{ marginBottom: 16 }}
            message={`项目智能体名额 ${projectQuota.used}/${projectQuota.limit}`}
            description={
              projectQuota.remaining > 0
                ? `当前项目还可以创建 ${projectQuota.remaining} 个智能体。`
                : "当前项目智能体名额已用完，可归档/删除不需要的智能体或升级权益。"
            }
          />
        )}
        <Table
          rowKey="id"
          loading={loading}
          dataSource={agents}
          columns={columns}
          pagination={false}
          locale={{
            emptyText:
              postChatProjects.length === 0
                ? "暂无 PostChat 项目"
                : "暂无智能体，先创建一个",
          }}
          onRow={(record) => ({
            onClick: () => setSelectedAgentId(record.id),
            style: {
              cursor: "pointer",
              background: record.id === selectedAgentId ? "#fafcff" : undefined,
            },
          })}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={10}>
          <Card title="智能体详情" variant="outlined" style={{ height: "100%" }}>
            {selectedAgent ? (
              <Space direction="vertical" size={14} style={{ width: "100%" }}>
                <div>
                  <Title level={4} style={{ marginBottom: 8 }}>
                    {selectedAgent.name}
                  </Title>
                  <Space wrap>
                    <Tag color={(statusMap[selectedAgent.status || ""] || statusMap.draft).color}>
                      {(statusMap[selectedAgent.status || ""] || statusMap.draft).label}
                    </Tag>
                    <Tag color={selectedAgent.is_public ? "blue" : "default"}>
                      {selectedAgent.is_public ? "公开访问" : "私有"}
                    </Tag>
                    {selectedAgent.share_enable && <Tag color="cyan">已开启分享</Tag>}
                  </Space>
                </div>

                <div>
                  <Text type="secondary">描述</Text>
                  <Paragraph style={{ marginTop: 6, marginBottom: 0 }}>
                    {selectedAgent.description || "暂无描述"}
                  </Paragraph>
                </div>

                <div>
                  <Text type="secondary">系统提示词</Text>
                  <Paragraph
                    style={{ marginTop: 6, marginBottom: 0, whiteSpace: "pre-wrap" }}
                    ellipsis={{ rows: 6, expandable: true, symbol: "展开" }}
                  >
                    {selectedAgent.system_prompt || "暂无系统提示词"}
                  </Paragraph>
                </div>

                <div>
                  <Text type="secondary">关联知识库</Text>
                  <div style={{ marginTop: 8 }}>
                    {selectedAgent.knowledge_base_ids?.length ? (
                      <Space wrap>
                        {selectedAgent.knowledge_base_ids.map((projectId) => {
                          const project = projects.find((item) => item.id === projectId);
                          return <Tag key={projectId}>{project?.name || `项目 ${projectId}`}</Tag>;
                        })}
                      </Space>
                    ) : (
                      <Text type="secondary">未绑定知识库</Text>
                    )}
                  </div>
                </div>

                <div>
                  <Text type="secondary">分享令牌</Text>
                  <Paragraph copyable style={{ marginTop: 6, marginBottom: 0 }}>
                    {selectedAgent.share_token || "未生成"}
                  </Paragraph>
                </div>
              </Space>
            ) : (
              <Empty description="请选择一个智能体" />
            )}
          </Card>

          <Card title="对话记录" variant="outlined" style={{ marginTop: 16 }}>
            {selectedAgent ? (
              <Spin spinning={conversationsLoading}>
                {hiddenMessageCount > 0 && (
                  <Button
                    block
                    style={{ marginBottom: 12 }}
                    onClick={() => setVisibleMessageCount((count) => count + MESSAGE_WINDOW_STEP)}
                  >
                    加载更早 {Math.min(hiddenMessageCount, MESSAGE_WINDOW_STEP)} 条
                  </Button>
                )}
                <List
                  style={{ maxHeight: 620, overflowY: "auto", paddingRight: 8 }}
                  locale={{ emptyText: "暂无对话记录" }}
                  dataSource={conversations}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        paddingLeft: 12,
                        paddingRight: 12,
                        borderRadius: 8,
                        marginBottom: 8,
                        background:
                          item.id === selectedConversationId ? "#f6ffed" : "#fafafa",
                        border:
                          item.id === selectedConversationId
                            ? "1px solid #b7eb8f"
                            : "1px solid #f0f0f0",
                      }}
                      actions={[
                        <Button
                          key="delete"
                          type="text"
                          danger
                          size="small"
                          onClick={() => handleDeleteConversation(item.id)}
                        >
                          删除
                        </Button>,
                      ]}
                      onClick={() => setSelectedConversationId(item.id)}
                    >
                      <List.Item.Meta
                        title={item.title || `对话 ${item.id}`}
                        description={`消息 ${item.messageCount} 条 · Token ${item.tokenUsed}`}
                      />
                    </List.Item>
                  )}
                />
              </Spin>
            ) : (
              <Empty description="选中智能体后查看对话" />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={14}>
          <Card title="运行智能体" variant="outlined">
            {selectedAgent ? (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Input.TextArea
                  rows={5}
                  value={runQuery}
                  onChange={(event) => setRunQuery(event.target.value)}
                  placeholder="输入要交给智能体的问题、任务或上下文"
                />
                <Space>
                  <Button type="primary" loading={runLoading} onClick={handleRunAgent}>
                    运行
                  </Button>
                  <Button onClick={() => setRunQuery("")}>清空</Button>
                </Space>

                {(runResult || streamingText) && (
                  <Card size="small" style={{ background: "#fafcff" }}>
                    <Space direction="vertical" size={8} style={{ width: "100%" }}>
                      <Space wrap>
                        {runResult ? (
                          <Tag color="success">{runResult.status || "completed"}</Tag>
                        ) : (
                          <Tag color="processing">生成中…</Tag>
                        )}
                        {runResult?.modelUsed && <Tag>{runResult.modelUsed}</Tag>}
                        {runResult?.cached && <Tag color="cyan">缓存命中</Tag>}
                      </Space>
                      {streamingReasoning && (
                        <Collapse
                          size="small"
                          style={{ width: "100%" }}
                          items={[
                            {
                              key: "reasoning",
                              label: "Reasoning",
                              children: <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{streamingReasoning}</Paragraph>,
                            },
                          ]}
                        />
                      )}
                      <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                        {runResult?.answer ||
                          streamingText ||
                          JSON.stringify(runResult?.output || {}, null, 2)}
                      </Paragraph>
                    </Space>
                  </Card>
                )}
              </Space>
            ) : (
              <Empty description="请先从上方列表选择智能体" />
            )}
          </Card>

          <Card title="会话消息" variant="outlined" style={{ marginTop: 16 }}>
            {selectedConversationId ? (
              <Spin spinning={messagesLoading}>
                <List
                  locale={{ emptyText: "暂无消息" }}
                  dataSource={visibleMessages}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 16,
                              background: item.role === "assistant" ? "#e6f4ff" : "#fff7e6",
                              color: item.role === "assistant" ? "#1677ff" : "#d48806",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i
                              className={
                                item.role === "assistant"
                                  ? "ri-robot-2-line"
                                  : "ri-user-3-line"
                              }
                            />
                          </div>
                        }
                        title={
                          <Space wrap>
                            <Text strong>
                              {item.role === "assistant" ? "智能体" : "用户"}
                            </Text>
                            <Text type="secondary">
                              {new Date(item.createdAt).toLocaleString("zh-CN")}
                            </Text>
                          </Space>
                        }
                        description={
                          <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                            {item.content || "-"}
                          </Paragraph>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Spin>
            ) : (
              <Empty description="请选择一条对话查看消息" />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title={editingAgent ? "编辑项目主 Agent" : "新建项目主 Agent"}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingAgent ? "保存" : "创建"}
        cancelText="取消"
        confirmLoading={submitting}
        width={720}
      >
        <Form form={form} layout="vertical" initialValues={{ isPublic: false }}>
          {!editingAgent && (
            <Form.Item name="template" label="创建方式">
              <Select
                options={effectiveTemplates.map((template) => ({
                  value: template.value,
                  label: template.label,
                }))}
                onChange={(value) => applyAgentTemplate(value, form.getFieldValue("projectId"))}
              />
            </Form.Item>
          )}
          <Form.Item name="agentType" hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="projectId"
                label="所属项目"
                rules={[{ required: true, message: "请选择所属项目" }]}
              >
                <Select
                  options={projectOptions}
                  placeholder="选择智能体所属项目"
                  onChange={(value) => {
                    const currentKnowledgeIds = form.getFieldValue("knowledgeBaseIds") || [];
                    if (currentKnowledgeIds.length === 0) {
                      form.setFieldValue("knowledgeBaseIds", [value]);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="名称"
                rules={[{ required: true, message: "请输入智能体名称" }]}
              >
                <Input placeholder="例如：客服助理" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="model" label="模型">
                <Select
                  showSearch
                  options={modelOptions}
                  placeholder="选择可用模型"
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="icon" label="图标">
                <Select options={iconOptions} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="描述这个智能体的职责和适用场景" />
          </Form.Item>

          <Form.Item name="systemPrompt" label="系统提示词">
            <Input.TextArea
              rows={6}
              placeholder="输入系统提示词，用来约束智能体输出风格和行为"
            />
          </Form.Item>

          <Form.Item name="knowledgeBaseIds" label="关联知识库项目">
            <Select
              mode="multiple"
              allowClear
              options={knowledgeOptions}
              placeholder="可选，绑定当前工作区内的知识库项目"
            />
          </Form.Item>

          <Form.Item name="isPublic" label="允许公开访问" valuePropName="checked">
            <Switch checkedChildren="公开" unCheckedChildren="私有" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Agents;
